// Links inside the iPhone and Android apps.
//
// The apps ship only the React game (app/www). The website's static pages (how to play, the rules
// guides, the shop, the score keeper, /app ...) are deleted from the app build, so a plain link to
// one of them loaded the app's index.html with no matching route: a blank page (Sam, 4 Nov 2026).
// Inside the apps, every link that isn't one of the game's own screens now opens outside the app:
// static pages on https://playrafiki.com in the phone's browser, other websites likewise, and
// mailto:/tel: in the mail or phone app. Capacitor hands any navigation to a host other than the
// app's own (or a non-web scheme) to the system, so setting location.href is enough on both
// platforms. The website and the portal builds are untouched (installNativeLinkHandler is a no-op
// outside the native apps).

export const SITE = "https://playrafiki.com";

// The game's own screens (React routes in index.tsx). These keep working inside the app.
const IN_APP_PREFIXES = [
  "/login", "/game/", "/create", "/play", "/daily", "/challenge/", "/signup", "/admin",
  "/forgot-password", "/change-username", "/change-password/", "/cookie-policy", "/privacy-notice",
  "/privacy-policy", "/terms", "/learn", "/attribution", "/policies",
];

export const isInAppPath = (path: string): boolean => {
  const p = path.split(/[?#]/)[0] || "/";
  if (p === "/" || p === "") return true;
  return IN_APP_PREFIXES.some((pre) => (pre.endsWith("/") ? p.startsWith(pre) : p === pre || p.startsWith(pre + "/")));
};

// Where a link should go when tapped inside the native app: null = let the app handle it as usual,
// otherwise the URL to hand to the system (browser, mail app, phone).
export const nativeLinkTarget = (href: string | null | undefined): string | null => {
  if (!href) return null;
  const h = href.trim();
  if (!h || h.startsWith("#") || /^javascript:/i.test(h)) return null;
  if (/^(mailto|tel|sms):/i.test(h)) return h;
  if (/^https?:\/\//i.test(h)) {
    try {
      const u = new URL(h);
      // Our own site: the game's screens stay in the app, everything else opens in the browser.
      if (/(^|\.)playrafiki\.com$/i.test(u.hostname) && isInAppPath(u.pathname)) return null;
      if (/^localhost$|^capacitor$/i.test(u.hostname)) {
        return isInAppPath(u.pathname) ? null : SITE + u.pathname + u.search + u.hash;
      }
    } catch (e) {
      return h;
    }
    return h;
  }
  if (h.startsWith("//")) return "https:" + h;
  if (h.startsWith("/")) return isInAppPath(h) ? null : SITE + h;
  return null; // relative links like "rules" aren't used in the game
};

const isNative = (): boolean =>
  typeof window !== "undefined" && Boolean((window as any).Capacitor?.isNativePlatform?.());

export const openOutside = (url: string): void => {
  const hook = (window as any).__rafikiOpenOutside; // lets the app-build link check record taps
  if (typeof hook === "function") hook(url);
  else window.location.href = url;
};

// For code that navigates without a link (e.g. the start screen's "Get the deck" button).
export const goTo = (path: string): void => {
  const target = isNative() ? nativeLinkTarget(path) : null;
  if (target) openOutside(target);
  else window.location.href = path;
};

let installed = false;
export const installNativeLinkHandler = (): void => {
  if (installed || !isNative() || typeof document === "undefined") return;
  installed = true;
  document.addEventListener(
    "click",
    (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      const el = e.target as Element | null;
      const a = el && (el.closest ? (el.closest("a[href]") as HTMLAnchorElement | null) : null);
      if (!a) return;
      const target = nativeLinkTarget(a.getAttribute("href"));
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      openOutside(target);
    },
    true
  );
};
